/**
 * Mission Management Page
 * Monitor, manage, and act on all missions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  IconButton,
  Chip,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, StatusBadge, TablePagination, CopyableText } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox, DatePicker } from '../../components/ui';
import { MissionService } from '../../services/mission/MissionService';
import { formatDateTime } from '../../utils/dateUtils';
import { formatGhostTierDisplay, getCallSignDisplayName } from '../../utils/generalUtils';
import { missionTypeLabels } from '../../services/mock/mockMission';
import type { Mission, PaginatedMissions } from '../../types/mission';
import { MissionStatus } from '../../types/mission';

const statusOptions: { value: MissionStatus; label: string }[] = [
  // { value: MissionStatus.PENDING, label: 'Pending' },
  { value: MissionStatus.PENDING_ADMIN_REVIEW, label: 'Pending Admin Review' },
  { value: MissionStatus.SUBMITTED, label: 'Submitted' },
  { value: MissionStatus.MISSION_IN_PROGRESS, label: 'Mission In Progress' },
  { value: MissionStatus.COMPLETED, label: 'Completed' },
  { value: MissionStatus.UNDER_REVIEW, label: 'Under Review' },
  { value: MissionStatus.CLOSED, label: 'Closed' },
  { value: MissionStatus.CANCELLED, label: 'Cancelled' },
  { value: MissionStatus.EXPIRED, label: 'Expired' },
];

// Maps each UI status filter to one or more API statuses
const statusToApiStatuses: Partial<Record<MissionStatus, MissionStatus[]>> = {
  [MissionStatus.PENDING_ADMIN_REVIEW]: [MissionStatus.PENDING_ADMIN_REVIEW],
  [MissionStatus.SUBMITTED]: [MissionStatus.SUBMITTED],
  [MissionStatus.MISSION_IN_PROGRESS]: [MissionStatus.MISSION_IN_PROGRESS, MissionStatus.GHOST_ASSIGNED],
  [MissionStatus.COMPLETED]: [MissionStatus.COMPLETED],
  [MissionStatus.UNDER_REVIEW]: [MissionStatus.UNDER_REVIEW],
  [MissionStatus.CLOSED]: [MissionStatus.CLOSED],
  [MissionStatus.CANCELLED]: [MissionStatus.CANCELLED],
  [MissionStatus.EXPIRED]: [MissionStatus.EXPIRED],
};

const getApiStatusFilters = (selectedStatuses: Set<MissionStatus>): MissionStatus[] => {
  if (selectedStatuses.size === 0) return [];
  return Array.from(selectedStatuses).flatMap(displayStatus => statusToApiStatuses[displayStatus] ?? [])
};

interface DateRange {
  start: string;
  end: string;
}

const MissionManagementPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<MissionStatus>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  // Date Range state
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const fetchMissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedMissions = await MissionService.getMissions(
        page,
        pageSize,
        {
          status: getApiStatusFilters(selectedStatuses),
          search: debouncedSearchValue || undefined,
          dateRange: dateRange
            ? {
              start: dateRange.start ? new Date(dateRange.start) : undefined,
              end: dateRange.end ? new Date(dateRange.end + 'T23:59:59') : undefined,
            }
            : undefined,
        }
      );
      setMissions(result.data);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      setMissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, selectedStatuses, debouncedSearchValue, dateRange]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const handleStatusToggle = (status: MissionStatus) => {
    const newSelected = new Set(selectedStatuses);
    if (newSelected.has(status)) {
      newSelected.delete(status);
    } else {
      newSelected.add(status);
    }
    setSelectedStatuses(newSelected);
    setPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleViewMission = (mission: Mission) => {
    navigate(`/missions/${mission.id}`);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  // Date Range handlers
  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const newRange = {
      ...(dateRange || { start: '', end: '' }),
      [field]: value,
    };
    setDateRange(newRange.start || newRange.end ? newRange : null);
    setPage(1);
  };

  const handleClearDateRange = () => {
    setDateRange(null);
    setPage(1);
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: theme.typography.h4.fontWeight,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Mission Management
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          Monitor, manage, and act on all missions across the platform.
        </Typography>
      </Box>

      {/* Search and Date Range */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        {/* Search Box */}
        <SearchBox
          placeholder="Search by Mission Code, Title or Call Sign..."
          value={searchValue}
          onChange={handleSearch}
          containerSx={{ width: { xs: '100%', md: 400 } }}
        />

        {/* Date Range Inputs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <DatePicker
            value={dateRange?.start || ''}
            onChange={(e) => handleDateRangeChange('start', e.target.value)}
            sx={{ width: 150 }}
          />
          <Typography sx={{ color: theme.palette.text.secondary }}>~</Typography>
          <DatePicker
            value={dateRange?.end || ''}
            onChange={(e) => handleDateRangeChange('end', e.target.value)}
            sx={{ width: 150 }}
          />
          {dateRange && (
            <IconButton
              size="small"
              onClick={handleClearDateRange}
              sx={{ color: theme.palette.text.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Status Filters (Multi-select with Chips) */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: theme.typography.subtitle2.fontWeight,
            marginBottom: theme.spacing(1.5),
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle2.fontSize,
          }}
        >
          Filter by Status:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          {statusOptions.map((option) => {
            const isSelected = selectedStatuses.has(option.value);
            return (
              <Chip
                key={option.value}
                label={option.label}
                onClick={() => handleStatusToggle(option.value)}
                onDelete={isSelected ? (e) => {
                  e.stopPropagation();
                  handleStatusToggle(option.value);
                } : undefined}
                clickable
                sx={{
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.background.paper,
                  color: isSelected
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.primary,
                  fontWeight: isSelected ? 500 : 400,
                  borderRadius: theme.shapes.borderRadius.lg,
                  border: `1px solid ${isSelected
                    ? theme.palette.primary.main
                    : theme.customColors.tableBorder
                    }`,
                  cursor: isSelected ? 'default' : 'pointer',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? theme.palette.primary.dark
                      : theme.customColors.tableRowHover,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: isSelected
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.secondary,
                    '&:hover': {
                      color: isSelected
                        ? theme.palette.primary.contrastText
                        : theme.palette.error.main,
                    },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Missions Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : missions.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Missions Found"
                message="There are no missions matching your criteria."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Mission Code</TableHeaderCell>
                      <TableHeaderCell>Mission Title</TableHeaderCell>
                      <TableHeaderCell>Mission Type</TableHeaderCell>
                      <TableHeaderCell>Category</TableHeaderCell>
                      <TableHeaderCell>Ghost Tier</TableHeaderCell>
                      <TableHeaderCell>Asset</TableHeaderCell>
                      <TableHeaderCell>Ghost</TableHeaderCell>
                      <TableHeaderCell>Candidate Ghost</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Start Date</TableHeaderCell>
                      <TableHeaderCell>End Date</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                      <TableHeaderCell>Updated</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missions.map((mission) => (
                      <TableRow key={mission.id}>
                        <TableCell>
                          <CopyableText
                            text={mission.code}
                            onTextClick={() => navigate(`/missions/${mission.id}`)}
                            sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          {mission.title}
                        </TableCell>
                        <TableCell>
                          {missionTypeLabels[mission.missionType]}
                        </TableCell>
                        <TableCell>
                          {mission.category}
                        </TableCell>
                        <TableCell>
                          {formatGhostTierDisplay(mission.ghostTier) || '-'}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {getCallSignDisplayName(mission.assetCallSign, 'Asset')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {mission.assetEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {mission.ghostId ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getCallSignDisplayName(mission.ghostCallSign, 'Ghost')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {mission.ghostEmail}
                              </Typography>
                            </Box>
                          ) : (
                            'Not Assigned'
                          )}
                        </TableCell>
                        <TableCell>
                          {mission.candidateGhostId ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getCallSignDisplayName(
                                  mission.candidateGhostCallSign,
                                  'Candidate Ghost'
                                )}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {mission.candidateGhostEmail}
                              </Typography>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={mission.status} />
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {mission.startDate ? formatDateTime(mission.startDate) : '-'}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {mission.endDate ? formatDateTime(mission.endDate) : '-'}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {formatDateTime(mission.createdAt)}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {formatDateTime(mission.updatedAt)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => handleViewMission(mission)}
                            sx={{
                              textTransform: 'none',
                              color: theme.palette.primary.main,
                              padding: 0,
                              minWidth: 'auto',
                              fontSize: theme.typography.body2.fontSize,
                              fontWeight: 400,
                              '&:hover': {
                                textDecoration: 'underline',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default MissionManagementPage;
